import React, { FC } from 'react'
import I18n from 'i18n'
import relationships from './parameterRelationships.json'
import { localized } from 'components/common/Locales'

interface Relationship {
  param1: string
  param2: string
  // Explanation per locale, e.g. { en: '...', nl: '...' }; English is the fallback.
  explanation: Record<string, string>
  source: string
  link: string | null
}

interface MetricOption {
  uniqueId?: string
}

interface ParameterKnowledgeBarProps {
  metric1?: MetricOption | null
  metric2?: MetricOption | null
}

const RELATIONSHIPS = relationships as Relationship[]

export const METRIC_KNOWLEDGE_KEY: Readonly<Record<string, string>> = Object.freeze({
  v2_daily: 'Sleep duration',
  v1_daily: 'Sleep quality',
  v8_daily: 'Resting heart rate',
  v7_daily: 'Readiness',
  v3_daily: 'Fatigue',
  v4_daily: 'Stress',
  v5_daily: 'Muscle soreness',
  v6_daily: 'Mood',
  v9_training: 'Fun during training',
  v7_training: 'Satisfaction of the training',
  v5_training: 'Training duration',
  v6_training: 'RPE',
  wellbeingScore_wellbeing: 'Well-being score',
  srpe_training: 'sRPE',
  expectedRpe_scheduled: 'Expected RPE (scheduled)'
})

export const findRelationship = (key1: string, key2: string): Relationship | undefined =>
  RELATIONSHIPS.find(
    (r) =>
      (r.param1 === key1 && r.param2 === key2) ||
      (r.param1 === key2 && r.param2 === key1)
  )

const ParameterKnowledgeBar: FC<ParameterKnowledgeBarProps> = ({ metric1, metric2 }) => {
  const uniqueId1 = metric1?.uniqueId
  const uniqueId2 = metric2?.uniqueId
  const key1 = uniqueId1 !== undefined ? METRIC_KNOWLEDGE_KEY[uniqueId1] : undefined
  const key2 = uniqueId2 !== undefined ? METRIC_KNOWLEDGE_KEY[uniqueId2] : undefined

  if (key1 === undefined || key2 === undefined || key1 === key2) return null

  const relationship = findRelationship(key1, key2)
  if (relationship === undefined) return null

  const explanation = localized(relationship.explanation) ?? ''

  const sourceLabel = I18n.t(
    'components.dashboards.coach_individual.load.knowledge.source_label'
  )

  return (
    <div className='row parameter-knowledge-bar__row'>
      <div className='col s12'>
        <div className='parameter-knowledge-bar'>
          {explanation.split('\n').map((line, idx) => (
            <p key={idx} className='parameter-knowledge-bar__text'>
              {line}
            </p>
          ))}
          <p className='parameter-knowledge-bar__source'>
            {sourceLabel}:{' '}
            {relationship.link !== null && relationship.link !== ''
              ? (
                <a
                  href={relationship.link}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='parameter-knowledge-bar__link'
                >
                  {relationship.source}
                </a>
                )
              : (
                  relationship.source
                )}
          </p>
        </div>
      </div>
    </div>
  )
}

export default ParameterKnowledgeBar
